import { camelCase as _camelCase } from 'lodash';

import { type ISelectOption } from '@sine/sine-ui-components';

type MappableItem = Record<string, any>;
type MappableList<T extends MappableItem> = Array<T>;
type DefaultMappableItem = { name: string; id: string };

export interface ISelectOptionVisitorTypesRequirements extends ISelectOption {
  emailRequired: boolean;
  mobileRequired: boolean;
  detailsRequired: boolean;
}

/**
 * Remaps an array of objects into Select-dropdown-compatible options.
 * @param list The items to remap.
 * @param labelKey The index key for the label. Defaults to `name`.
 * @param valueKey The index key for the value. Defaults to `id`.
 * @returns A list of Select-dropdown-compatible options.
 */
export default function mapToDropdownOptions<
  T extends MappableItem = DefaultMappableItem,
  LabelKey extends keyof T = 'name',
  ValueKey extends keyof T = 'id',
>(
  list: MappableList<T> = [],
  labelKey: LabelKey = 'name' as LabelKey,
  valueKey: ValueKey = 'id' as ValueKey,
): Array<ISelectOption> {
  return list.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
  }));
}

/**
 * Remaps an array of objects into Select-dropdown-compatible options with Icon
 * @param list The items to remap.
 * @param uicIcon Icon component to display on each option.
 * @param labelKey The index key for the label. Defaults to `name`.
 * @param valueKey The index key for the value. Defaults to `id`.
 * @returns A list of Select-dropdown-compatible options.
 */
export function mapToDropdownOptionsWithIcon<
  T extends MappableItem = DefaultMappableItem,
  LabelKey extends keyof T = 'name',
  ValueKey extends keyof T = 'id',
>(
  list: MappableList<T> = [],
  uicIcon: JSX.Element,
  labelKey: LabelKey = 'name' as LabelKey,
  valueKey: ValueKey = 'id' as ValueKey,
): Array<ISelectOption> {
  return list.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    icon: uicIcon,
  }));
}

/**
 * Remaps an array of objects into Select-dropdown-compatible options with Color
 * @param list The items to remap.
 * @param labelKey The index key for the label. Defaults to `name`.
 * @param valueKey The index key for the value. Defaults to `id`.
 * @returns A list of Select-dropdown-compatible options.
 */
export function mapToDropdownOptionsWithColor<
  T extends MappableItem = DefaultMappableItem,
  LabelKey extends keyof T = 'name',
  ValueKey extends keyof T = 'id',
>(
  list: MappableList<T> = [],
  colorMaps: Record<string, any>,
  labelKey: LabelKey = 'name' as LabelKey,
  valueKey: ValueKey = 'id' as ValueKey,
): Array<ISelectOption> {
  return list.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    color: {
      background: `${colorMaps[_camelCase(item.color)].background}`,
      text: `${colorMaps[_camelCase(item.color)].text}`,
    },
  }));
}

/**
 * Remaps an array of objects into Select-dropdown-compatible options with Visitor Type Requirements (emailRequired and mobileNumberRequired).
 * @param list The items to remap.
 * @param labelKey The index key for the label. Defaults to `name`.
 * @param valueKey The index key for the value. Defaults to `id`.
 * @returns A list of Select-dropdown-compatible options.
 */
export function mapToDropdownOptionsWithVtRequirements<
  T extends MappableItem = DefaultMappableItem,
  LabelKey extends keyof T = 'name',
  ValueKey extends keyof T = 'id',
>(
  list: MappableList<T> = [],
  labelKey: LabelKey = 'name' as LabelKey,
  valueKey: ValueKey = 'id' as ValueKey,
): Array<ISelectOptionVisitorTypesRequirements> {
  return list.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    emailRequired: item.emailRequired,
    mobileRequired: item.mobileRequired,
    detailsRequired: item.detailsRequired,
  }));
}
